import { getWorkspace } from '../session/sessionStorage'
import { useFieldsInterpolationsFetcher } from './fields'
import { useRawFieldsInterpolationsFetcher } from './rawFields'
import { useRawRepliesInterpolationsFetcher } from './rawReplies'
import { useRepliesInterpolationsFetcher } from './replies'

// Tokens extras (e revalidação dos campos adicionais) valem só para o gatilho "resposta editada";
// para `event` o resultado é idêntico ao anterior.
const REPLY_EDITED_ENTITY = 'service_order_reply'
const REPLY_EDITED_FETCH_POLICY = 'cache-and-network'

// Dados da atividade que existem no cadastro/edição (Prioridade, Tag e Ativo); só para "resposta editada".
const replyEditedActivityTokens = [
  {
    name: 'Prioridade da atividade',
    id: '{{event.service_order.priority.name}}',
    type: 'string',
    group: 'Atividade'
  },
  { name: 'Tag da atividade', id: '{{event.service_order.tag.name}}', type: 'string', group: 'Atividade' },
  { name: 'Ativo da atividade', id: '{{event.service_order.asset.name}}', type: 'string', group: 'Atividade' }
]

export const useEventInterpolationsFetcher = (skip, checklistId, placeType, opts, origin) => {
  const isReplyEdited = origin === REPLY_EDITED_ENTITY
  const fetchPolicy = isReplyEdited ? REPLY_EDITED_FETCH_POLICY : undefined
  const repliesOptions = useRepliesInterpolationsFetcher(skip, 'self.service_order.replies', checklistId, opts)
  const fieldsOptions = useFieldsInterpolationsFetcher(skip, 'self.service_order.field_values', 'service_orders', {
    fetchPolicy
  })
  const placeFieldsOptions = useFieldsInterpolationsFetcher(
    skip || !placeType,
    'self.service_order.place.field_values',
    placeType,
    {
      group: 'Destino',
      fetchPolicy
    }
  )
  const workspace = getWorkspace()

  if (skip) return []

  return [
    { name: 'Código da atividade', id: '{{event.service_order.yearly_id}}', type: 'string', group: 'Atividade' },
    { name: 'Nome da atividade', id: '{{event.service_order.name}}', type: 'string', group: 'Atividade' },
    {
      name: 'Descrição da atividade',
      id: '{{event.service_order.description}}',
      type: 'string',
      group: 'Atividade'
    },
    {
      name: 'Nome do questionário',
      id: '{{event.service_order.checklist.name}}',
      type: 'string',
      group: 'Atividade'
    },
    { name: 'Status', id: '{{event.service_order.status.name}}', type: 'string', group: 'Atividade' },
    { name: 'Nome do projeto', id: '{{event.service_order.project.name}}', type: 'string', group: 'Atividade' },
    { name: 'Nome do local', id: '{{place.name}}', type: 'string', group: 'Atividade' },
    ...(isReplyEdited ? replyEditedActivityTokens : []),
    {
      name: 'Link público de resumo',
      id: `https://${workspace}.btime.io/resume/{{service_order.id}}?t={{service_order.resume_token}}`,
      type: 'string',
      group: 'Atividade'
    },
    {
      name: 'Link público de execução',
      id: `https://${workspace}.btime.io/service-orders/{{service_order.id}}/execution?t={{service_order.execution_token}}`,
      type: 'string',
      group: 'Atividade'
    },

    { name: 'Nome do usuário', id: '{{anonymous event.service_order.user.name}}', type: 'string', group: 'Usuário' },
    { name: 'Email do usuário', id: '{{event.service_order.user.email}}', type: 'string', group: 'Usuário' },
    { name: 'Grupo do usuário', id: '{{user.contract.group.name}}', type: 'string', group: 'Usuário' },

    { name: 'Nome do destino', id: '{{event.service_order.place.name}}', type: 'string', group: 'Destino' },
    {
      name: 'Nome completo do destino',
      id: '{{event.service_order.place.resume}} - {{event.service_order.place.name}}',
      type: 'string',
      group: 'Destino'
    },
    ...placeFieldsOptions,

    { name: 'Data de agendamento', id: '{{event.service_order.scheduling}}', type: 'datetime', group: 'Datas' },
    { name: 'Data de abertura', id: '{{event.service_order.start_date}}', type: 'datetime', group: 'Datas' },
    { name: 'Data de conclusão', id: '{{event.service_order.end_date}}', type: 'datetime', group: 'Datas' },

    ...fieldsOptions,
    ...repliesOptions
  ]
}

export const useEventRawInterpolationsFetcher = (skip, checklistId, origin) => {
  const isReplyEdited = origin === REPLY_EDITED_ENTITY
  const fetchPolicy = isReplyEdited ? REPLY_EDITED_FETCH_POLICY : undefined
  const rawRepliesOptions = useRawRepliesInterpolationsFetcher(skip, 'self.service_order.replies', checklistId)
  const rawFieldsOptions = useRawFieldsInterpolationsFetcher(
    skip,
    'self.service_order.field_values',
    'service_orders',
    {
      fetchPolicy
    }
  )

  if (skip) return []

  return [
    { name: 'Usuário da atividade atualizada', id: '::{{event.service_order.user_id}}', type: 'raw_user' },

    { name: 'Local da atividade atualizada', id: '::{{event.service_order.place_id}}', type: 'raw_place' },
    { name: 'Ativo da atividade atualizada', id: '::{{event.service_order.asset_id}}', type: 'raw_asset' },
    { name: 'Metadata', id: '::{{event.service_order.metadata}}', type: 'raw_metadata' },

    {
      name: 'Questionário da atividade atualizada',
      id: '::{{event.service_order.checklist_id}}',
      type: 'raw_checklist'
    },

    { name: 'Data de agendamento', id: '::{{event.service_order.scheduling}}', type: 'raw_datetime', group: 'Datas' },
    { name: 'Data de abertura', id: '::{{event.service_order.start_date}}', type: 'raw_datetime', group: 'Datas' },
    { name: 'Data de conclusão', id: '::{{event.service_order.end_date}}', type: 'raw_datetime', group: 'Datas' },

    ...rawFieldsOptions,
    ...rawRepliesOptions
  ]
}
